/* eslint-disable @typescript-eslint/restrict-template-expressions */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Typography, useTheme, Tooltip } from '@mui/material';
import {
  type GridColumns,
  type GridValidRowModel,
  type GridSortModel,
} from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { getUserContribution } from 'service/ContributionService';
import { colorTheme } from '../../../../theme';
import { CustomDataGrid } from 'components';
import { type IEmployeeProps } from 'types';
import { strings } from 'i18n/Strings';
import { useSelector, useDispatch } from 'react-redux';
import { CurrencyFormater } from 'components/helpers/commonFunctions/CurrencyFormater';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import {
  getFortnightLabel,
  getMonthFullName,
  getWeekLabel,
} from 'utils/Constants';

type GridRowData = Record<string, unknown>;
const Contribution = (props: any): JSX.Element => {
  const employeeData = props?.employeeData;
  const dispatch = useDispatch();
  const currentTheme = useTheme();
  const colors = colorTheme(currentTheme.palette.mode);
  const [contributionData, setContributionData] = useState<
    IEmployeeProps[] | unknown
  >([]);
  const [pageCount, setPageCount] = useState(null);
  const pageInfo = useSelector((state) => state?.pagination?.userContributions);
  const getUserContributionData = (): void => {
    if (employeeData.userId) {
      getUserContribution(
        employeeData.userId,
        Number(pageInfo?.page) + 1,
        Number(pageInfo?.pageSize),
        pageInfo?.sortBy,
        pageInfo?.sortOrder,
      )
        .then((res: unknown) => {
          if (typeof res !== 'undefined') {
            const { userContributions, count } = res as {
              userContributions: IEmployeeProps[];
              count: number;
            };
            if (userContributions) {
              setPageCount(count);
              setContributionData(userContributions);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };

  useEffect(() => {
    getUserContributionData();
  }, [
    pageInfo?.page,
    pageInfo?.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
  ]);

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'dateOfUpload',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.dateUploadLabel}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.contributionFile?.uploadDate}
        </Typography>
      ),
    },
    {
      field: 'firstName',
      width: 200,
      sortable: true,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.nameColumn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.user.userName}
        </Typography>
      ),
    },
    {
      field: 'periodDate',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.periodDate}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.contributionFile?.period}
        </Typography>
      ),
    },
    {
      field: 'frequencyType',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.frequencyType}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row?.contributionFile?.frequencyType}{' '}
        </Typography>
      ),
    },
    {
      field: 'period',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.periodlabel}
        </Typography>
      ),
      renderCell: ({ row }) => {
        let value: string;
        switch (row.contributionFile.frequencyType) {
          case 'monthly':
            value = `${getMonthFullName(row.contributionFile.frequencyValue)} ${
              row.contributionFile.year
            }`;
            break;
          case 'fortnightly':
            value =
              row.contributionFile.frequencyValue >= 1 &&
              row.contributionFile.frequencyValue <= 26
                ? `${getFortnightLabel(
                    Number(row.contributionFile.frequencyValue),
                  )} ${row.contributionFile.year}`
                : '-';
            break;
          case 'weekly':
            value =
              row.contributionFile.frequencyValue >= 1 &&
              row.contributionFile.frequencyValue <= 52
                ? `${getWeekLabel(
                    Number(row.contributionFile.frequencyValue),
                  )} ${row.contributionFile.year}`
                : '-';
            break;
          default:
            value = `${row.contributionFile.frequencyValue} ${row.contributionFile.year}`;
        }

        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {value}
          </Typography>
        );
      },
    },
    {
      field: 'employeeContribution',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeContribution}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.employeeContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {CurrencyFormater(Number(row.employeeContribution))}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'employerContribution',
      width: 200,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employerContribution}
        </Typography>
      ),
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row.employerContribution} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {CurrencyFormater(Number(row.employerContribution))}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'status',
      width: 100,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Tooltip title={row.totalContribution} placement="top">
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {row?.contributionFile?.status}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'userContributions',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'userContributions', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'userContributions', value: 0 }));
  };

  return (
    <>
      <Box>
        <CustomDataGrid
          title="Contribution"
          getRowId={(row: any) => `${String(row.contributionFileId)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          columns={columns}
          rows={contributionData as GridRowData[]}
          pageCount={pageCount}
          currentPage="userContributions"
          sortingMode={strings.displayText.sortingServer}
          onSortModelChange={(mode: any) => {
            handleSortChange(mode);
          }}
          sortModel={checkSortingValues(pageInfo)}
        />
      </Box>
    </>
  );
};

export default Contribution;
